import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ConfigurationUploadState,
  updateConfigurationUploadModalState,
  updateModalsCloseDrawerModal,
  updateShowConfigurationUploadModal,
} from "@src/reducers/your-reducer";
import { OtaDrawerDataInterface } from "@src/types/ota-update";
import { useI18N } from "@src/language/language-context";
import { validateFields } from "@src/cloud-connect/utils/validate-fields";
import {
  errorSnackbar,
  warningSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { postConfigurationUploadUseCase } from "@src/cloud-connect/ota-update/use-cases/post-upload-configuration";
import { RootState } from "@src/reducers/store";
import { isFilenameUsableUseCase } from "@src/cloud-connect/ota-update/use-cases/is-filename-usable";

type useOtaConfigurationDrawerModalProps = {
  inputValues: OtaDrawerDataInterface;
  setInputValues: React.Dispatch<React.SetStateAction<OtaDrawerDataInterface>>;
};

export const useOtaConfigurationDrawerModal = ({
  inputValues,
  setInputValues,
}: useOtaConfigurationDrawerModalProps) => {
  const i18n = useI18N();
  const dispatch = useDispatch();

  const connectionView = useSelector(
    (state: RootState) => state.yourSlice?.connectionView,
  );
  const authContext = useSelector(
    (state: RootState) => state.yourSlice?.authContext,
  );

  const texts = {
    configurationVersionText: i18n(
      "modules.otaDrawerForm.configurationVersionText",
    ),
    configurationNameText: i18n("modules.otaDrawerForm.configurationNameText"),
    releaseNewConfigurationText: i18n(
      "modules.otaConfigurationsDrawerModal.releaseNewConfigurationText",
    ),
    releaseConfigurationDetailsText: i18n(
      "modules.otaConfigurationsDrawerModal.releaseConfigurationDetailsText",
    ),
    releaseConfigurationText: i18n(
      "modules.otaConfigurationsDrawerModal.releaseConfigurationText",
    ),
    cancelText: i18n("modules.otaConfigurationsDrawerModal.cancelText"),
    fixValidateFieldText: i18n(
      "modules.otaConfigurationsDrawerModal.fixValidateFieldText",
    ),
    uploadFileText: i18n("modules.otaDrawerForm.uploadFileText"),
    uploadFailedText: i18n(
      "modules.otaConfigurationsDrawerModal.uploadFailedText",
    ),
  };

  const handleCloseDrawerModal = useCallback((): void => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "otaConfigurationDrawerModal",
      }),
    );
  }, [dispatch]);

  const startConfigurationCreation = useCallback(async () => {
    try {
      const formData = new FormData();

      formData.append("source_id", connectionView.source.source_id);
      formData.append("uploader_id", authContext.userId);
      formData.append("version", inputValues.version);
      formData.append("size", String(inputValues?.file?.size));
      formData.append("filename", inputValues?.name);
      formData.append("release_notes_intro", inputValues.release_notes_intro);
      formData.append(
        "release_notes_improvements",
        inputValues.release_notes_improvements,
      );
      formData.append("release_notes_fixes", inputValues.release_notes_fixes);
      formData.append(
        "release_notes_general",
        inputValues.release_notes_general,
      );
      formData.append("type", "CONFIGURATION");
      formData.append("file", inputValues?.file);

      const result = await postConfigurationUploadUseCase(formData);

      if (result.success) {
        dispatch(
          updateConfigurationUploadModalState(
            ConfigurationUploadState.CONFIGURATION_UPLOAD_COMPLETED,
          ),
        );
      } else {
        dispatch(
          updateConfigurationUploadModalState(
            ConfigurationUploadState.CONFIGURATION_UPLOAD_FAILED,
          ),
        );
      }
    } catch {
      errorSnackbar(texts.uploadFailedText, 5000);
    }
  }, [
    authContext.userId,
    connectionView.source.source_id,
    dispatch,
    inputValues?.file,
    inputValues?.name,
    inputValues.release_notes_fixes,
    inputValues.release_notes_general,
    inputValues.release_notes_improvements,
    inputValues.release_notes_intro,
    inputValues.version,
    texts.uploadFailedText,
  ]);

  const handleOpenConfigurationUploadModal = useCallback((): void => {
    dispatch(updateShowConfigurationUploadModal(true));
    handleCloseDrawerModal();
  }, [dispatch, handleCloseDrawerModal]);

  const handleSubmit = useCallback(async () => {
    const fieldsToValidate = [
      {
        key: "name",
        label: texts.configurationNameText,
      },
    ];

    const emptyFields = validateFields(fieldsToValidate, inputValues);
    const missingFields = [...emptyFields];

    if (!inputValues.file) {
      missingFields.push(texts.uploadFileText);
    }

    if (missingFields.length > 0) {
      warningSnackbar(
        `${texts.fixValidateFieldText} ${missingFields.join(", ")}.`,
        5000,
      );
      return;
    }

    const response = await isFilenameUsableUseCase(
      inputValues?.file?.name,
      "CONFIGURATION",
    );

    if (!response.success) {
      warningSnackbar(
        `Unable to use file "${inputValues?.file?.name}". Please check the file name.`,
        5000,
      );
      return;
    }

    handleOpenConfigurationUploadModal();
    startConfigurationCreation();
  }, [
    handleOpenConfigurationUploadModal,
    inputValues,
    startConfigurationCreation,
    texts.configurationNameText,
    texts.fixValidateFieldText,
    texts.uploadFileText,
  ]);

  return {
    texts,
    handleCloseDrawerModal,
    handleSubmit,
    setInputValues,
  };
};
